import { Injectable, inject } from '@angular/core';
import { ISbStoryData } from '@storyblok/js';
import { firstValueFrom, from, lastValueFrom, map } from 'rxjs';
import Stripe from 'stripe';
import { enterpriseStripeProduct, freeStripeProduct, freeStripeProductPrice } from '../billing-consts';
import { GROWTHBOOK } from '../growthbook';
import { Product } from '../types/product.type';
import { PricingService } from './pricing.service';
import { StoryblokService } from './storyblok.service';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private storyblokService = inject(StoryblokService);
  private pricingService = inject(PricingService);
  private growthBook = inject(GROWTHBOOK);

  public async getAllProducts(): Promise<Product[]> {
    const [products, prices] = await Promise.all([
      lastValueFrom(this.storyblokService.getStories({ content_type: 'PricingTechnicalLink' })),
      firstValueFrom(this.pricingService.getPrices('main')),
    ]);
    return products.map(product => this.convertProduct(product, prices)).filter(Boolean);
  }

  private convertProduct(productLink: ISbStoryData, mainPrices: Stripe.Price[]): Product {
    const allPricesForProduct = mainPrices.filter(price =>
      (price.product as Stripe.Product).name.includes(productLink.name)
    );
    const monthlyPrice = this.pricingService.getDefaultPrice('month', allPricesForProduct);
    const yearlyPrice = this.pricingService.getDefaultPrice('year', allPricesForProduct);
    const prices = [monthlyPrice, yearlyPrice].filter(Boolean);

    const isEnterprise = productLink.name.toLowerCase().includes('enterprise');
    const isFree = !isEnterprise && !prices.length;
    const stripeProduct = isFree
      ? freeStripeProduct
      : isEnterprise
        ? enterpriseStripeProduct
        : (prices[0]?.product as Stripe.Product);

    const product: Product = {
      ...stripeProduct,
      storyblokLinkId: productLink.uuid,
      name: productLink.name,
      color: productLink.content.color,
      prices: isFree ? [freeStripeProductPrice] : prices,
      isFree,
      isEnterprise,
    };
    return product;
  }

  public getFeaturedProducts() {
    return from(this.getAllProducts()).pipe(
      map(allProducts => {
        const defaultProductNames = this.growthBook.getFeatureValue('featured-products', []);
        return defaultProductNames
          .map(name => allProducts.find(p => p.name.toLowerCase().includes(name.toLowerCase())))
          .filter(Boolean);
      })
    );
  }
  public getAllListedProducts() {
    return from(this.getAllProducts()).pipe(
      map(allProducts => {
        const defaultProductNames = this.growthBook.getFeatureValue('all-listed-products', []);
        return defaultProductNames
          .map(name => allProducts.find(p => p.name.toLowerCase().includes(name.toLowerCase())))
          .filter(Boolean);
      })
    );
  }

  public async getRecommendedUpgradeProduct(currentProductId: string): Promise<Product> {
    const [defaultProducts, allProducts] = await Promise.all([
      firstValueFrom(this.getFeaturedProducts()),
      this.getAllProducts(),
    ]);
    const currentProduct = allProducts.find(product => product.id === currentProductId);
    const currentProductName = currentProduct.name.toLowerCase();
    const recommendedProductName = (() => {
      if (!currentProduct || currentProduct.isFree) return 'plus';
      if (currentProductName === 'plus') return 'pro';
    })();
    return defaultProducts.find(product => product.name.toLowerCase() === recommendedProductName);
  }
}
